import React from 'react';
import { Modal, ModalSize } from 'components/common';
import { Bot, HubSpotBot, SyncAnalysis } from 'apis/users';

type BotDetailsModalProps = {
  open: boolean;
  handleClose: VoidFunction;
  botInfo: Bot[];
  hubspotBots: HubSpotBot[];
  syncAnalysis: SyncAnalysis;
  dealId: string | null;
  onResync: VoidFunction;
  isSyncing: boolean;
};

const BotDetailsModal = ({
  open,
  handleClose,
  botInfo,
  hubspotBots,
  syncAnalysis,
  dealId,
  onResync,
  isSyncing,
}: BotDetailsModalProps) => {
  // Get all unique slot numbers from both DB and HubSpot
  const allSlots = Array.from(
    new Set([
      ...botInfo.map((b) => b.slot),
      ...hubspotBots.map((b) => b.slot),
    ])
  ).sort((a, b) => a - b);

  const getDbBot = (slot: number) => botInfo.find((b) => b.slot === slot);
  const getHsBot = (slot: number) => hubspotBots.find((b) => b.slot === slot);

  const getSlotStatus = (slot: number) => {
    const dbBot = getDbBot(slot);
    const hsBot = getHsBot(slot);

    if (!dbBot && hsBot && hsBot.name && hsBot.link) {
      return { text: '+ Will be added', color: 'text-green-600' };
    }
    if (dbBot && !hsBot) {
      return { text: '- Will be removed', color: 'text-red-600' };
    }
    if (!dbBot && hsBot && (!hsBot.name || !hsBot.link)) {
      return { text: '⚠ Incomplete', color: 'text-gray-500' };
    }
    if (dbBot && hsBot) {
      if (dbBot.name === hsBot.name && dbBot.link === hsBot.link) {
        return { text: '✓ In sync', color: 'text-green-600' };
      } else {
        return { text: '⚠ Out of sync', color: 'text-orange-600' };
      }
    }
    return { text: '-', color: 'text-gray-400' };
  };

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      title={`Bot Sync Details - HubSpot Deal: ${dealId || 'N/A'}`}
      showHeader
      showFooter
      submitButtonTitle="Resync"
      submitting={isSyncing}
      handleSubmit={onResync}
      size={ModalSize.xxl}
    >
      <div className="p-6">
        {/* Safety check - show message if no sync analysis available */}
        {!syncAnalysis && (
          <div className="p-4 bg-gray-50 rounded text-gray-600 text-center">
            No sync analysis available. Please try syncing first.
          </div>
        )}

        {syncAnalysis && (
          <>
            {/* Issues/Warnings */}
            {syncAnalysis.issues.length > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
            <div className="font-medium text-yellow-800 mb-2">⚠ Issues Found:</div>
            <ul className="list-disc list-inside text-yellow-700 text-sm">
              {syncAnalysis.issues.map((issue, idx) => (
                <li key={idx}>{issue.message}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Comparison Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b-2 border-gray-200">
                <th className="p-3 text-left font-medium text-gray-700 w-16">Slot</th>
                <th className="p-3 text-left font-medium text-gray-700">Database</th>
                <th className="p-3 text-left font-medium text-gray-700">HubSpot</th>
                <th className="p-3 text-left font-medium text-gray-700 w-32">Status</th>
              </tr>
            </thead>
            <tbody>
              {allSlots.map((slot) => {
                const dbBot = getDbBot(slot);
                const hsBot = getHsBot(slot);
                const status = getSlotStatus(slot);

                return (
                  <tr key={slot} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="p-3 align-top font-medium text-gray-600">{slot}</td>
                    <td className="p-3 align-top">
                      {dbBot ? (
                        <div className="text-sm">
                          <div className="font-medium text-gray-800 mb-1">{dbBot.name}</div>
                          <a
                            href={dbBot.link}
                            target="_blank"
                            rel="noreferrer"
                            className="text-accent-main hover:underline break-words"
                          >
                            {dbBot.link}
                          </a>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="p-3 align-top">
                      {hsBot && (hsBot.name || hsBot.link) ? (
                        <div className="text-sm">
                          <div className="font-medium text-gray-800 mb-1">
                            {hsBot.name || <span className="text-red-500">Missing name</span>}
                          </div>
                          {hsBot.link ? (
                            <a
                              href={hsBot.link}
                              target="_blank"
                              rel="noreferrer"
                              className="text-accent-main hover:underline break-words"
                            >
                              {hsBot.link}
                            </a>
                          ) : (
                            <span className="text-red-500 text-sm">Missing link</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className={`p-3 align-top text-sm font-medium ${status.color}`}>
                      {status.text}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="mt-4 p-4 bg-gray-50 rounded text-sm">
          <div className="font-medium text-gray-700 mb-2">Summary:</div>
          <div className="text-gray-600">
            <div>Complete bots: {syncAnalysis.details.complete_bots}</div>
            <div>Incomplete bots: {syncAnalysis.details.incomplete_bots}</div>
            <div>Empty slots: {syncAnalysis.details.empty_slots}</div>
            {syncAnalysis.details.changes.added.length > 0 && (
              <div>Will add: {syncAnalysis.details.changes.added.join(', ')}</div>
            )}
            {syncAnalysis.details.changes.updated.length > 0 && (
              <div>Will update: {syncAnalysis.details.changes.updated.join(', ')}</div>
            )}
            {syncAnalysis.details.changes.removed.length > 0 && (
              <div>Will remove: {syncAnalysis.details.changes.removed.join(', ')}</div>
            )}
          </div>
        </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default BotDetailsModal;
